import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    DatePicker,
    Empty,
    Input,
    message,
    Modal,
    Space,
    Table,
    Tabs,
    Tooltip,
    Upload,
    Spin
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    CalendarOutlined,
    CopyOutlined,
    DeleteOutlined,
    FileOutlined,
    SnippetsOutlined,
    UploadOutlined,
    SaveOutlined,
    UndoOutlined,
    FilterOutlined,
    CheckCircleTwoTone
} from '@ant-design/icons';
import axios from 'axios';
import { getConfig } from '../../../config';
import { useProjectContext } from '../../../context/ProjectContext';
import { useDirtyContext } from '../../../context/DirtyContext';

import dayjs from 'dayjs';
import 'dayjs/locale/de';
import locale from 'antd/es/date-picker/locale/de_DE';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import updateLocale from 'dayjs/plugin/updateLocale';

// 💅 CSS
import './ContractDateConfiguration.css';

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(updateLocale);
dayjs.locale('de');
dayjs.updateLocale('de', { weekStart: 1 });

const { API_BASE_URL } = getConfig();
const { TabPane } = Tabs;

// -------------------- Typen --------------------

interface WorkContractChange {
    id: number;
    origin: string;
    changed: string;
    changeUser: string;
    timestamp: string;
    filename: string;
    abacusPath: string;
    persistanceUnit: string;
}

interface WorkContractRowFromAPI {
    protocolDescription: string;
    mountingDateContracted: string;        // "YYYY/WW"
    mountingDateContractedAsDate: string;  // "DD.MM.YYYY" (nicht verwendet)
    kapaIdentifierSlack: string;           // "229999/00/01"
    orderCardId: string;                   // "2299990001"
    historyOfContractChanges: WorkContractChange[];
}

interface WorkContractRow extends WorkContractRowFromAPI {
    key: string;
    newAgreedWeek?: string;              // "YYYY/WW"
    newAgreedWeekDayjs?: dayjs.Dayjs | null;
    newDocFile?: File | null;
    newDocName?: string;
}

interface CopyBuffer {
    sourceKey: string;
    newAgreedWeek?: string;
    newAgreedWeekDayjs?: dayjs.Dayjs | null;
    newDocFile?: File | null;
    newDocName?: string;
}

interface HistoryEntryAPI {
    project: string;
    supplement: string;
    protocol: string;
    newMountingDate: string; // "YYYY/WW"
    file: string;            // "<Kein File>" oder Dateiname
    abacusFile: string;
    changeDate: string;      // "DD.MM.YYYY HH:mm:ss"
    changeUser: string;
    protocolDescription: string;
    highlighted: boolean;
    id: number;
    protocolAsInt: number;
    supplementAsInt: number;
    sortingId: number;       // Zeitstempel als Zahl
}

interface HistoryRow {
    key: string;
    sp: string;             // "SS/PP" mit führender 0
    supplementAsInt: number;
    protocolAsInt: number;
    protocolDescription: string;
    newMountingDate: string;
    file: string;
    abacusFile: string;
    changeDate: string;
    changeUser: string;
    sortingId: number;
    highlighted: boolean;
}

// -------------------- Helfer --------------------

const parsePrFromKapa = (kapaIdentifierSlack: string) => {
    return kapaIdentifierSlack.split('/').slice(1).join('/');
};

const weekStringToDayjs = (wk?: string) => {
    if (!wk) return null;
    const [y, w] = wk.split('/').map(n => parseInt(n, 10));
    if (Number.isNaN(y) || Number.isNaN(w) || w < 1) return null;
    const jan4 = dayjs(`${y}-01-04`);
    return jan4.startOf('isoWeek').add(w - 1, 'week');
};

const dayjsToWeekString = (d: dayjs.Dayjs) => {
    const s = d.startOf('isoWeek');
    return `${s.isoWeekYear()}/${String(s.isoWeek()).padStart(2, '0')}`;
};

const disablePastWeeks = (current: dayjs.Dayjs) =>
    current && current.endOf('isoWeek').isBefore(dayjs().startOf('day'));

// 25 Zeichen Basename; Extension bleibt unverändert
const truncateFilename = (name?: string) => {
    if (!name) return '';
    const MAX = 25;

    const dot = name.lastIndexOf('.');
    if (dot <= 0 || dot === name.length - 1) {
        const base = name.slice(0, MAX);
        return name.length > MAX ? `${base}[…]` : name;
    }

    const base = name.slice(0, dot);
    const ext = name.slice(dot + 1);
    const shortBase = base.length > MAX ? `${base.slice(0, MAX)}[…]` : base;
    return `${shortBase}.${ext}`;
};

const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const res = (reader.result as string) || '';
            const idx = res.indexOf('base64,');
            resolve(idx >= 0 ? res.substring(idx + 7) : res);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

// -------------------- Komponente --------------------

const ContractDateConfiguration: React.FC = () => {
    const { selectedProject } = useProjectContext();
    const { setDirty } = useDirtyContext();

    const [activeTab, setActiveTab] = useState<string>('overview');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [rows, setRows] = useState<WorkContractRow[]>([]);
    const [originalRows, setOriginalRows] = useState<WorkContractRow[]>([]);
    const [copyBuffer, setCopyBuffer] = useState<CopyBuffer | null>(null);

    // Filters (Overview)
    const [filterPr, setFilterPr] = useState('');
    const [filterDesc, setFilterDesc] = useState('');
    const [filterContracted, setFilterContracted] = useState('');

    // Date modal
    const [dateModalOpen, setDateModalOpen] = useState(false);
    const [dateModalRowKey, setDateModalRowKey] = useState<string | null>(null);
    const [dateModalWeek, setDateModalWeek] = useState<dayjs.Dayjs | null>(null);

    // History
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
    const [showOnlyLatestRelevant, setShowOnlyLatestRelevant] = useState(false);

    // -------------------- Datenladen: Overview --------------------

    const load = async () => {
        if (!selectedProject || selectedProject === 'Alle') {
            setRows([]);
            setOriginalRows([]);
            return;
        }
        setIsLoading(true);
        try {
            const url = `${API_BASE_URL}/getWorkContractData?projectNo=${selectedProject}`;
            const { data } = await axios.get<WorkContractRowFromAPI[]>(url);
            const mapped: WorkContractRow[] = data.map((r, idx) => ({
                ...r,
                key: r.orderCardId ?? `${r.kapaIdentifierSlack}-${idx}`,
                newAgreedWeek: undefined,
                newAgreedWeekDayjs: null,
                newDocFile: null,
                newDocName: undefined
            }));
            setRows(mapped);
            setOriginalRows(mapped);
            setCopyBuffer(null);
        } catch {
            message.error('Fehler beim Laden der Werkvertragstermine.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject]);

    // -------------------- Filterung: Overview --------------------

    const filteredRows = useMemo(() => {
        return rows.filter(r => {
            const pr = parsePrFromKapa(r.kapaIdentifierSlack);
            const okPr = !filterPr || pr.toLowerCase().includes(filterPr.toLowerCase());
            const okDesc =
                !filterDesc ||
                (r.protocolDescription ?? '').toLowerCase().includes(filterDesc.toLowerCase());
            const okContracted =
                !filterContracted ||
                (r.mountingDateContracted ?? '').toLowerCase().includes(filterContracted.toLowerCase());
            return okPr && okDesc && okContracted;
        });
    }, [rows, filterPr, filterDesc, filterContracted]);

    // -------------------- Änderungen / Save-State --------------------

    const changedRows = useMemo(
        () => rows.filter(r => r.newAgreedWeek || r.newDocFile),
        [rows]
    );

    const canSave = useMemo(() => {
        if (changedRows.length === 0) return false;
        return changedRows.every(r => !!r.newAgreedWeek && !!r.newDocFile);
    }, [changedRows]);

    // -------------------- Aktionen: Overview --------------------

    const openDateModalFor = (rowKey: string) => {
        const row = rows.find(r => r.key === rowKey);
        if (!row) return;
        setDateModalRowKey(rowKey);
        setDateModalWeek(row.newAgreedWeekDayjs ?? weekStringToDayjs(row.newAgreedWeek) ?? null);
        setDateModalOpen(true);
    };

    const confirmDateModal = () => {
        if (!dateModalRowKey) return;
        const w = dateModalWeek ? dayjsToWeekString(dateModalWeek) : undefined;

        setRows(prev =>
            prev.map(r =>
                r.key === dateModalRowKey
                    ? { ...r, newAgreedWeek: w, newAgreedWeekDayjs: dateModalWeek ?? null }
                    : r
            )
        );
        setDirty(true);
        setDateModalOpen(false);
        setDateModalRowKey(null);
        setDateModalWeek(null);
    };

    const cancelDateModal = () => {
        setDateModalOpen(false);
        setDateModalRowKey(null);
        setDateModalWeek(null);
    };

    const handleUpload = (rowKey: string, file: File) => {
        setRows(prev =>
            prev.map(r =>
                r.key === rowKey ? { ...r, newDocFile: file, newDocName: file.name } : r
            )
        );
        setDirty(true);
        return false;
    };

    const resetRow = (rowKey: string) => {
        setRows(prev =>
            prev.map(r =>
                r.key === rowKey
                    ? {
                        ...r,
                        newAgreedWeek: undefined,
                        newAgreedWeekDayjs: null,
                        newDocFile: null,
                        newDocName: undefined
                    }
                    : r
            )
        );
        setDirty(true);
    };

    // Kopieren nur anzeigen, wenn es einen neuen Termin gibt
    const startCopyFrom = (rowKey: string) => {
        const src = rows.find(r => r.key === rowKey);
        if (!src || !src.newAgreedWeek) return;
        setCopyBuffer({
            sourceKey: rowKey,
            newAgreedWeek: src.newAgreedWeek,
            newAgreedWeekDayjs:
                src.newAgreedWeekDayjs ?? (src.newAgreedWeek ? weekStringToDayjs(src.newAgreedWeek) : null),
            newDocFile: src.newDocFile ?? null,
            newDocName: src.newDocName
        });
        message.success('Daten kopiert. Wähle nun eine Zeile zum Einfügen.');
    };

    const pasteTo = (rowKey: string) => {
        if (!copyBuffer) return;
        if (copyBuffer.sourceKey === rowKey) {
            message.info('Quelle und Ziel sind identisch.');
            return;
        }
        setRows(prev =>
            prev.map(r =>
                r.key === rowKey
                    ? {
                        ...r,
                        newDocFile: copyBuffer.newDocFile ?? null,
                        newDocName: copyBuffer.newDocName,
                        newAgreedWeek: copyBuffer.newAgreedWeek,
                        newAgreedWeekDayjs: copyBuffer.newAgreedWeekDayjs ?? null
                    }
                    : r
            )
        );
        setDirty(true);
    };

    const clearCopyMode = () => setCopyBuffer(null);

    // -------------------- Speichern + Bestätigungs-Dialog --------------------

    const askCopyDateChoice = (): Promise<boolean> =>
        new Promise((resolve) => {
            Modal.confirm({
                title: 'Datum in Auftragskarten übernehmen?',
                content:
                    'Möchtest du die neu vereinbarten Montagentermine zusätzlich in die Auftragskarten kopieren?',
                okText: 'Ja',
                cancelText: 'Nein',
                centered: true,
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
            });
        });

    const handleSave = async () => {
        if (!selectedProject || selectedProject === 'Alle') return;
        if (!canSave) {
            message.warning('Bitte für jede geänderte Zeile sowohl den neuen Termin als auch ein Dokument angeben.');
            return;
        }

        try {
            const copyDate = await askCopyDateChoice();

            setIsSaving(true);

            const items = await Promise.all(
                changedRows.map(async (r) => {
                    const contentBase64 = await fileToBase64(r.newDocFile as File);
                    const contentType = (r.newDocFile as File).type || 'application/pdf';
                    return {
                        content: contentBase64,
                        contentType,
                        filename: r.newDocName || (r.newDocFile as File).name,
                        newDate: r.newAgreedWeek as string,
                        oldDate: r.mountingDateContracted,
                        orderCardId: r.orderCardId
                    };
                })
            );

            const payload = {
                user: '120',
                copyDate,
                mountingContractData: items,
                auth: 'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU'
            };

            await axios.post(`${API_BASE_URL}/saveChangedMountingContracts`, payload);

            message.success('Werkvertragstermine erfolgreich gespeichert.');
            setDirty(false);
            await load();
        } catch (err) {
            console.error(err);
            message.error('Speichern der Werkvertragstermine fehlgeschlagen.');
        } finally {
            setIsSaving(false);
        }
    };

    // -------------------- Spalten: Overview --------------------

    const topHeaderCell = () => ({ className: 'align-top' });

    const columns: ColumnsType<WorkContractRow> = [
        {
            title: (
                <div>
                    Pr.
                    <Input
                        allowClear
                        placeholder="Suche"
                        onChange={e => setFilterPr(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="wc-input-full"
                        disabled={isSaving}
                    />
                </div>
            ),
            onHeaderCell: topHeaderCell,
            dataIndex: 'kapaIdentifierSlack',
            key: 'pr',
            width: 90,
            render: (val: string) => {
                const pr = parsePrFromKapa(val);
                return (
                    <Space>
                        <span>{pr}</span>
                    </Space>
                );
            },
            sorter: (a, b) =>
                parsePrFromKapa(a.kapaIdentifierSlack).localeCompare(
                    parsePrFromKapa(b.kapaIdentifierSlack)
                )
        },
        {
            title: (
                <div>
                    Bezeichnung
                    <Input
                        allowClear
                        placeholder="Suche"
                        onChange={e => setFilterDesc(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="wc-input-full"
                        disabled={isSaving}
                    />
                </div>
            ),
            onHeaderCell: topHeaderCell,
            dataIndex: 'protocolDescription',
            key: 'protocolDescription',
            width: 360,
            sorter: (a, b) =>
                (a.protocolDescription ?? '').localeCompare(b.protocolDescription ?? '')
        },
        {
            title: (
                <div>
                    Vereinbarter Montagetermin
                    <Input
                        allowClear
                        placeholder="Suche"
                        onChange={e => setFilterContracted(e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="wc-input-full"
                        disabled={isSaving}
                    />
                </div>
            ),
            onHeaderCell: topHeaderCell,
            dataIndex: 'mountingDateContracted',
            key: 'mountingDateContracted',
            width: 240,
            align: 'right',
            render: (v: string) => (
                <span className="wc-text-rightfill">{v}</span>
            ),
            sorter: (a, b) => (a.mountingDateContracted ?? '').localeCompare(b.mountingDateContracted ?? '')
        },
        {
            title: 'Vereinbarter Montagetermin (Neu)',
            onHeaderCell: topHeaderCell,
            dataIndex: 'newAgreedWeek',
            key: 'newAgreedWeek',
            width: 220,
            render: (_: any, record) => {
                const display = record.newAgreedWeek ? record.newAgreedWeek : '—';
                return (
                    <div className="wc-flex-end">
                        <span className="wc-newdate-display">{display}</span>
                        <Tooltip title="Termin auswählen/ändern (ISO-KW)">
                            <Button
                                icon={<CalendarOutlined />}
                                onClick={() => openDateModalFor(record.key)}
                                disabled={isSaving}
                            />
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            title: 'Dokument anhängen',
            onHeaderCell: topHeaderCell,
            key: 'doc',
            width: 360,
            render: (_: any, record) => {
                const hasDoc = !!record.newDocFile;
                const text = hasDoc
                    ? truncateFilename(record.newDocName)
                    : 'kein Dokument';
                return (
                    <div className="wc-file-row">
                        <Upload
                            beforeUpload={(file) => handleUpload(record.key, file)}
                            showUploadList={false}
                            multiple={false}
                            disabled={isSaving}
                        >
                            <Button icon={<UploadOutlined />} disabled={isSaving}>Upload</Button>
                        </Upload>
                        <span
                            className={`wc-file-name ${hasDoc ? '' : 'wc-file-name-empty'}`}
                            title={record.newDocName}
                        >
              {text}
            </span>
                    </div>
                );
            }
        },
        {
            title: 'Kopieren',
            onHeaderCell: topHeaderCell,
            key: 'copy',
            width: 170,
            render: (_: any, record) => {
                const canCopyThisRow = !!record.newAgreedWeek;
                if (!canCopyThisRow && !copyBuffer) return <span className="wc-muted">—</span>;

                if (copyBuffer) {
                    const isSource = copyBuffer.sourceKey === record.key;
                    return (
                        <Space>
                            <Tooltip title={isSource ? 'Quelle' : 'In diese Zeile einfügen'}>
                                <Button
                                    icon={isSource ? <CopyOutlined /> : <SnippetsOutlined />}
                                    type={isSource ? 'default' : 'primary'}
                                    onClick={() => (!isSource ? pasteTo(record.key) : undefined)}
                                    disabled={isSource || isSaving}
                                >
                                    {isSource ? 'Quelle' : 'Einfügen'}
                                </Button>
                            </Tooltip>
                            <Button onClick={clearCopyMode} disabled={isSaving}>Beenden</Button>
                        </Space>
                    );
                }

                return (
                    <Tooltip title="Diese Zeile kopieren (Termin & optional Dokument)">
                        <Button
                            icon={<CopyOutlined />}
                            onClick={() => startCopyFrom(record.key)}
                            disabled={!canCopyThisRow || isSaving}
                        >
                            Kopieren
                        </Button>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Reset',
            onHeaderCell: topHeaderCell,
            key: 'reset',
            width: 100,
            render: (_: any, record) => {
                if (!record.newAgreedWeek) return <span className="wc-muted">—</span>;
                return (
                    <Tooltip title="Neu-Termin & Dokument entfernen">
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => resetRow(record.key)}
                            disabled={isSaving}
                        />
                    </Tooltip>
                );
            }
        }
    ];

    // -------------------- Historie: Laden & Aufbereitung --------------------

    const loadHistory = async () => {
        if (!selectedProject || selectedProject === 'Alle') {
            setHistoryRows([]);
            return;
        }
        setIsHistoryLoading(true);
        try {
            const url = `${API_BASE_URL}/getHistoryOfWorkContractData?projectNo=${selectedProject}`;
            const { data } = await axios.get<HistoryEntryAPI[]>(url);

            const mapped: HistoryRow[] = data.map((h) => {
                const sp = `${String(h.supplementAsInt).padStart(2, '0')}/${String(h.protocolAsInt).padStart(2, '0')}`;
                return {
                    key: String(h.id),
                    sp,
                    supplementAsInt: h.supplementAsInt,
                    protocolAsInt: h.protocolAsInt,
                    protocolDescription: h.protocolDescription,
                    newMountingDate: h.newMountingDate,
                    file: h.file,
                    abacusFile: h.abacusFile,
                    changeDate: h.changeDate,
                    changeUser: h.changeUser,
                    sortingId: h.sortingId,
                    highlighted: h.highlighted
                };
            });

            setHistoryRows(mapped);
        } catch {
            message.error('Fehler beim Laden der Historie.');
        } finally {
            setIsHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'history') {
            loadHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, selectedProject]);

    // Auswahl „letzter relevanter Termin“ pro Gruppe
    const latestRelevantPerGroup = useMemo(() => {
        const groups = new Map<string, HistoryRow[]>();
        for (const row of historyRows) {
            const key = `${row.supplementAsInt}-${row.protocolAsInt}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(row);
        }

        const pickLatest: HistoryRow[] = [];
        groups.forEach((arr) => {
            const withFile = arr
                .filter((r) => r.file && r.file !== '<Kein File>')
                .sort((a, b) => b.sortingId - a.sortingId);
            if (withFile.length > 0) {
                pickLatest.push(withFile[0]);
                return;
            }
            const abacus = arr
                .filter((r) => (r.changeUser || '').toLowerCase().includes('abacus'))
                .sort((a, b) => b.sortingId - a.sortingId);
            if (abacus.length > 0) {
                pickLatest.push(abacus[0]);
                return;
            }
            const newest = [...arr].sort((a, b) => b.sortingId - a.sortingId)[0];
            if (newest) pickLatest.push(newest);
        });
        return pickLatest;
    }, [historyRows]);

    // Set mit IDs der „relevanten“ Zeilen
    const highlightIdSet = useMemo(
        () => new Set(latestRelevantPerGroup.map(r => r.key)),
        [latestRelevantPerGroup]
    );

    // Spalten Historie
    const historyColumns: ColumnsType<HistoryRow> = [
        {
            title: 'Pr.',
            onHeaderCell: topHeaderCell,
            dataIndex: 'sp',
            key: 'sp',
            width: 120,
            sorter: (a, b) => {
                const sComp = a.supplementAsInt - b.supplementAsInt;
                if (sComp !== 0) return sComp;
                return a.protocolAsInt - b.protocolAsInt;
            },
            render: (_: any, record) => (
                <Space>
                    {highlightIdSet.has(record.key) ? (
                        <CheckCircleTwoTone twoToneColor="#1677ff" />
                    ) : null}
                    <span className={highlightIdSet.has(record.key) ? 'wc-bold' : undefined}>
            {record.sp}
          </span>
                </Space>
            )
        },
        {
            title: 'Bezeichnung',
            onHeaderCell: topHeaderCell,
            dataIndex: 'protocolDescription',
            key: 'protocolDescription',
            width: 360,
            render: (v: string, record) => (
                <span className={highlightIdSet.has(record.key) ? 'wc-bold' : undefined}>{v}</span>
            ),
            sorter: (a, b) => a.protocolDescription.localeCompare(b.protocolDescription)
        },
        {
            title: 'Vereinbarter Montagetermin',
            onHeaderCell: topHeaderCell,
            dataIndex: 'newMountingDate',
            key: 'newMountingDate',
            width: 180,
            align: 'right',
            render: (v: string, record) => (
                <span className={`wc-text-rightfill ${highlightIdSet.has(record.key) ? 'wc-bold' : ''}`}>
          {v}
        </span>
            ),
            sorter: (a, b) => a.newMountingDate.localeCompare(b.newMountingDate)
        },
        {
            title: 'File',
            onHeaderCell: topHeaderCell,
            dataIndex: 'file',
            key: 'file',
            width: 300,
            align: 'right',
            render: (v: string, record) => {
                const text = v && v !== '<Kein File>' ? truncateFilename(v) : '—';
                return (
                    <div className="wc-file-cell-right">
            <span
                title={v}
                className={`${v && v !== '<Kein File>' ? '' : 'wc-file-name-empty'} ${highlightIdSet.has(record.key) ? 'wc-bold' : ''}`}
            >
              {text}
            </span>
                        <span className="wc-file-cell-spacer" />
                    </div>
                );
            },
            sorter: (a, b) => (a.file || '').localeCompare(b.file || '')
        },
        {
            title: 'Geändert an',
            onHeaderCell: topHeaderCell,
            dataIndex: 'changeDate',
            key: 'changeDate',
            width: 200,
            sorter: (a, b) => a.sortingId - b.sortingId,
            render: (v: string, record) => (
                <span className={highlightIdSet.has(record.key) ? 'wc-bold' : undefined}>{v}</span>
            )
        },
        {
            title: 'Geändert von',
            onHeaderCell: topHeaderCell,
            dataIndex: 'changeUser',
            key: 'changeUser',
            width: 180,
            sorter: (a, b) => a.changeUser.localeCompare(b.changeUser),
            render: (v: string, record) => (
                <span className={highlightIdSet.has(record.key) ? 'wc-bold' : undefined}>{v}</span>
            )
        }
    ];

    // Datenquelle Historie
    const historyDataForTable = useMemo(() => {
        const base = showOnlyLatestRelevant ? latestRelevantPerGroup : historyRows;
        return [...base].sort((a, b) => {
            const sComp = a.supplementAsInt - b.supplementAsInt;
            if (sComp !== 0) return sComp;
            const pComp = a.protocolAsInt - b.protocolAsInt;
            if (pComp !== 0) return pComp;
            return b.sortingId - a.sortingId;
        });
    }, [historyRows, latestRelevantPerGroup, showOnlyLatestRelevant]);

    // -------------------- Render --------------------

    if (!selectedProject || selectedProject === 'Alle') {
        return (
            <div className="wc-container">
                <Empty description="Bitte ein Projekt auswählen, um Werkvertragstermine zu bearbeiten." />
            </div>
        );
    }

    return (
        <div className="wc-container">
            <Tabs activeKey={activeTab} onChange={key => !isSaving && setActiveTab(key)}>
                <TabPane tab="Änderung & Übersicht" key="overview">
                    <div className="wc-toolbar-right">
                        <Button icon={<UndoOutlined />} onClick={load} disabled={isSaving}>
                            Daten neu laden
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            disabled={!canSave || isSaving}
                            loading={isSaving}
                        >
                            Speichern
                        </Button>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={filteredRows}
                        loading={isLoading || isSaving}
                        pagination={{ pageSize: 100 }}
                        rowKey="key"
                    />

                    {/* Termin-Modal (Neu) */}
                    <Modal
                        title="Vereinbarten Montagetermin (Neu) festlegen"
                        open={dateModalOpen}
                        onOk={confirmDateModal}
                        onCancel={cancelDateModal}
                        okText="Übernehmen"
                        cancelText="Abbrechen"
                        maskClosable={false}
                        closable={!isSaving}
                        okButtonProps={{ disabled: isSaving }}
                        cancelButtonProps={{ disabled: isSaving }}
                    >
                        <div className="wc-modal-grid">
                            <div>
                                <label className="wc-label">Kalenderwoche</label>
                                <DatePicker
                                    picker="week"
                                    locale={locale}
                                    allowClear
                                    className="wc-input-full"
                                    value={dateModalWeek ?? null}
                                    format={(val) => {
                                        const s = val.startOf('isoWeek');
                                        return `${s.isoWeekYear()}/${String(s.isoWeek()).padStart(2, '0')}`;
                                    }}
                                    onChange={(val) => setDateModalWeek(val ?? null)}
                                    disabled={isSaving}
                                />
                            </div>
                            <div className="wc-hint">
                                Hinweis: Der Upload des Dokuments erfolgt in der Spalte „Dokument anhängen“. Bitte beachte, dass ohne hochgeladenes Dokument die Terminänderung keinen Einfluss auf den Bonus hat.
                            </div>
                        </div>
                    </Modal>
                </TabPane>

                <TabPane tab="Historie" key="history">
                    <div className="wc-toolbar-right">
                        <Button icon={<UndoOutlined />} onClick={loadHistory} disabled={isSaving}>
                            Daten neu laden
                        </Button>
                        <Button
                            icon={<FilterOutlined />}
                            type={showOnlyLatestRelevant ? 'primary' : 'default'}
                            onClick={() => setShowOnlyLatestRelevant(s => !s)}
                            disabled={isSaving}
                        >
                            Nur letzter relevanter Termin
                        </Button>
                    </div>

                    <Table
                        columns={historyColumns}
                        dataSource={historyDataForTable}
                        loading={isHistoryLoading || isSaving}
                        pagination={{ pageSize: 100 }}
                        rowKey="key"
                        rowClassName={(record) => (highlightIdSet.has(record.key) ? '' : 'workcontract-dim-row')}
                    />
                </TabPane>
            </Tabs>

            {/* Vollflächiges Speichern-Overlay */}
            <Modal
                open={isSaving}
                footer={null}
                closable={false}
                maskClosable={false}
                centered
                className="wc-saving-modal"
            >
                <div className="wc-saving-center">
                    <Spin size="large" />
                    <div className="wc-saving-text">
                        Daten werden gespeichert... Dies kann einige Sekunden dauern.
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ContractDateConfiguration;
